window['SoloProductRestrictionsClass'] = class SoloProductRestrictions {
	constructor() {
		this.isEnabled = window['theme']?.settings?.enable_solo_product_restrictions || false;
		this.notificationMessage =
			window['theme']?.settings?.solo_restriction_notification_message ||
			'This item cannot be purchased alone and has been removed from your cart.';
		this.notificationDuration = (window['theme']?.settings?.solo_restriction_notification_duration || 5) * 1000;

		this.debounceTimer = null;
		this.isValidating = false;
		this.lastCartState = null;
		this.performanceObserver = null;
		this.mutationObserver = null;
		this.lastItemCount = 0;

		if (this.isEnabled) {
			this.init();
		}
	}

	init() {
		this.setupEventListeners();
		this.validateCart();
		
		// Update button state after page loads (give Liquid time to render)
		setTimeout(() => this.updateAddToCartButtons(), 100);
	}

	setupEventListeners() {
		this.setupThemeEventListeners();
		this.setupDOMEventListeners();
		this.setupQuantityUpdateListeners();
		this.interceptFetchRequests();
		this.setupPerformanceObserver();
		this.setupMutationObserver();
		this.setupVisibilityListener();
		this.setupCheckoutProtection();
		this.setupNavigationProtection();
		this.setupAddToCartButtonUpdates();
	}

	setupThemeEventListeners() {
		// Theme event subscription function
		const subscribeToThemeEvent = typeof window['c'] === 'function' ? window['c'] : null;

		if (!subscribeToThemeEvent) {
			return;
		}

		subscribeToThemeEvent('cart:updated', (cartData) => this.handleCartUpdate(cartData));
		subscribeToThemeEvent('cart:item-added', (cartData) => this.handleCartUpdate(cartData));
	}

	setupDOMEventListeners() {
		document.addEventListener('cart:updated', (e) => this.handleCartUpdate(e['detail']));
	}

	setupQuantityUpdateListeners() {
		// Listen for quantity changes and removals - validate immediately for these
		document.addEventListener('quantity-update:remove', () => {
			this.validateCartImmediate();
		});

		document.addEventListener('quantity-update:subtract', () => {
			// Validate immediately when quantity decreases (item might be removed)
			this.validateCartImmediate();
		});

		// Theme event system also emits these events
		const subscribeToThemeEvent = typeof window['c'] === 'function' ? window['c'] : null;
		if (subscribeToThemeEvent) {
			subscribeToThemeEvent('quantity-update:remove', () => {
				this.validateCartImmediate();
			});
			subscribeToThemeEvent('quantity-update:subtract', () => {
				this.validateCartImmediate();
			});
		}
	}

	setupCheckoutProtection() {
		// Intercept checkout button clicks to validate before checkout
		document.addEventListener('click', (e) => {
			const target = e.target;
			if (!target || !(target instanceof Element)) {
				return;
			}

			const checkoutButton = target.closest('[href*="/checkout"], [name="checkout"], .cart__checkout, .checkout-button, [data-checkout]');
			if (checkoutButton) {
				e.preventDefault();
				e.stopPropagation();
				
				// Store the original action
				const originalHref = checkoutButton instanceof HTMLAnchorElement ? checkoutButton.href : null;
				const originalForm = checkoutButton instanceof HTMLButtonElement ? checkoutButton.form : null;
				
				// Validate immediately and wait for completion
				this.validateCartImmediate().then(() => {
					// Double-check cart state before allowing checkout
					this.fetchCart().then((cartData) => {
						const validationResult = this.checkCartItems(cartData.items);
						
						// Only allow checkout if cart is valid
						if (validationResult.restrictedItems.length === 0 || validationResult.hasNonRestrictedItems) {
							if (originalHref) {
								window.location.href = originalHref;
							} else if (originalForm) {
								const hiddenInput = document.createElement('input');
								
								hiddenInput.type = 'hidden';
								hiddenInput.name = 'checkout';
								hiddenInput.value = '';
								originalForm.appendChild(hiddenInput);

								originalForm.submit();
							} else if (checkoutButton instanceof HTMLElement) {
								// Create a new event to trigger the button
								const clickEvent = new MouseEvent('click', {
									bubbles: true,
									cancelable: true,
									view: window
								});
								checkoutButton.dispatchEvent(clickEvent);
							}
						} else {
							// Cart still invalid - show notification and don't proceed
							this.showNotification();
						}
					});
				});
			}
		}, true); // Use capture phase to catch early
	}

	setupVisibilityListener() {
		document.addEventListener('visibilitychange', () => {
			if (!document.hidden) {
				this.validateCartWithDebounce();
			}
		});
	}

	interceptFetchRequests() {
		// Intercept fetch to catch cart requests immediately
		const originalFetch = window.fetch;
		const self = this;

		window.fetch = async function(...args) {
			const response = await originalFetch.apply(this, args);
			const url = args[0];

			// If this is a cart request, validate after it completes
			if (typeof url === 'string' && self.isCartRequest(url)) {
				// Use a very short delay to allow cart to update on server
				setTimeout(() => self.validateCartImmediate(), 100);
			}

			return response;
		};
	}

	setupPerformanceObserver() {
		if (!('PerformanceObserver' in window)) {
			return;
		}

		try {
			const observer = new PerformanceObserver((list) => {
				for (const entry of list.getEntries()) {
					if (entry['initiatorType'] === 'fetch' && this.isCartRequest(entry.name)) {
						// Very short delay as backup
						setTimeout(() => this.validateCartImmediate(), 100);
					}
				}
			});

			observer.observe({ entryTypes: ['resource'] });
			this.performanceObserver = observer;
		} catch (error) {
			console.error('Error setting up performance observer:', error);
		}
	}

	setupMutationObserver() {
		// Watch for cart DOM changes as immediate signal
		const cartContainers = document.querySelectorAll('[js-cart-items], .cart__form-items, [data-section-type="cart"]');
		
		if (cartContainers.length === 0) {
			return;
		}

		const observer = new MutationObserver(() => {
			// Cart DOM changed - validate immediately
			this.validateCartImmediate();
		});

		cartContainers.forEach((container) => {
			observer.observe(container, {
				childList: true,
				subtree: true,
				attributes: false
			});
		});

		this.mutationObserver = observer;
	}

	setupNavigationProtection() {
		// Prevent navigation if cart has invalid state
		window.addEventListener('beforeunload', (e) => {
			if (this.isValidating) {
				return;
			}
		});
	}

	setupAddToCartButtonUpdates() {
		// Listen for cart updates to update Add to Cart button state
		const subscribeToThemeEvent = typeof window['c'] === 'function' ? window['c'] : null;
		
		if (subscribeToThemeEvent) {
			subscribeToThemeEvent('cart:updated', () => this.updateAddToCartButtons());
			subscribeToThemeEvent('cart:item-added', () => this.updateAddToCartButtons());
		}

		document.addEventListener('cart:updated', () => this.updateAddToCartButtons());

		// Also update on quantity changes
		document.addEventListener('quantity-update:remove', () => this.updateAddToCartButtons());
		document.addEventListener('quantity-update:subtract', () => this.updateAddToCartButtons());
		document.addEventListener('quantity-update:add', () => this.updateAddToCartButtons());
	}

	isCartRequest(url) {
		return url.includes('/cart/add') || url.includes('/cart/update') || url.includes('/cart/change');
	}

	handleCartUpdate(cartData) {
		if (cartData && JSON.stringify(cartData) !== JSON.stringify(this.lastCartState)) {
			const previousItemCount = this.lastCartState?.items?.length || 0;
			const currentItemCount = cartData?.items?.length || 0;

			this.lastCartState = cartData;

			// If item count decreased, validate immediately (no debounce)
			// This prevents race condition when removing non-restricted items
			if (currentItemCount < previousItemCount) {
				this.validateCartImmediate();
				return;
			}
		}
		this.validateCartWithDebounce();
	}

	/**
	 * Fetches cart data using available API or fallback fetch
	 */
	async fetchCart() {
		if (typeof window['cart'] !== 'undefined' && window['cart'].get) {
			return await window['cart'].get();
		}

		const response = await fetch('/cart.js', {
			method: 'GET',
			credentials: 'include',
		});

		return await response.json();
	}

	async updateCartItem(itemKey, quantity) {
		if (typeof window['cart'] !== 'undefined' && window['cart'].updateItem) {
			return await window['cart'].updateItem(itemKey, quantity);
		}

		return await fetch('/cart/change.js', {
			method: 'POST',
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				id: itemKey,
				quantity: quantity,
			}),
		});
	}

	validateCartWithDebounce() {
		if (this.debounceTimer) {
			clearTimeout(this.debounceTimer);
		}

		this.debounceTimer = setTimeout(() => {
			this.validateCart();
		}, 300);
	}

	/**
	 * Validates cart immediately without debounce
	 * Used for critical cases like item removal or checkout
	 */
	async validateCartImmediate() {
		if (this.debounceTimer) {
			clearTimeout(this.debounceTimer);
			this.debounceTimer = null;
		}
		await this.validateCart();
	}

	async validateCart() {
		if (this.isValidating) return;

		this.isValidating = true;

		try {
			const cartData = await this.fetchCart();
			const currentItemCount = cartData.items.length;

			// Update item count tracking
			if (currentItemCount !== this.lastItemCount) {
				this.lastItemCount = currentItemCount;
			}

			if (cartData.items.length === 0) {
				this.isValidating = false;
				// Update buttons when cart is empty
				this.updateAddToCartButtons();
				return;
			}

			await this.ensureProductData(cartData.items);

			const validationResult = this.checkCartItems(cartData.items);

			if (validationResult.restrictedItems.length > 0 && !validationResult.hasNonRestrictedItems) {
				await this.removeRestrictedItems(validationResult.restrictedItems);
				this.showNotification();
				// After removing items, validate again to ensure state is correct
				await this.validateCart();
			} else {
				// Cart is valid, update buttons
				this.updateAddToCartButtons();
			}
		} catch (error) {
			console.error('Error validating cart:', error);
		} finally {
			this.isValidating = false;
		}
	}

	checkCartItems(items) {
		const restrictedItems = [];
		let hasNonRestrictedItems = false;

		items.forEach((item) => {
			const productData = this.getProductData(item.product_id);

			if (this.isSoloRestricted(productData)) {
				restrictedItems.push(item);
			} else {
				hasNonRestrictedItems = true;
			}
		});

		return { restrictedItems, hasNonRestrictedItems };
	}

	async ensureProductData(cartItems) {
		if (!window['soloProductData']) {
			window['soloProductData'] = { cartItems: {} };
		}

		for (const item of cartItems) {
			const productId = String(item.product_id);

			if (window['soloProductData'].cartItems[productId]?.solo_restricted !== undefined) {
				continue;
			}

			if (this.useCurrentProductData(productId)) {
				continue;
			}

			await this.fetchProductDataFromAPI(item, productId);
		}
	}

	useCurrentProductData(productId) {
		if (window['currentProductData'] && String(window['currentProductData'].id) === productId) {
			window['soloProductData'].cartItems[productId] = {
				id: window['currentProductData'].id,
				solo_restricted: window['currentProductData'].solo_restricted,
				metafield_value: window['currentProductData'].metafield_value,
				has_tag: window['currentProductData'].has_tag,
				tags: window['currentProductData'].tags,
			};
			return true;
		}
		return false;
	}

	async fetchProductDataFromAPI(item, productId) {
		const productHandle = item.product_handle || item.handle;

		if (!productHandle) {
			return;
		}

		try {
			const response = await fetch(`/products/${productHandle}?view=solo-data`);
			const product = await response.json();

			const metafieldValue = product.metafield_value;
			const hasTag = product.has_tag || (product.tags && product.tags.includes('solo-restricted'));
			const isRestricted = product.solo_restricted === true || metafieldValue === true || metafieldValue === 'true' || hasTag;

			window['soloProductData'].cartItems[productId] = {
				id: product.id,
				solo_restricted: isRestricted,
				metafield_value: metafieldValue,
				has_tag: hasTag,
				tags: product.tags,
			};
		} catch (error) {
			console.error('Error fetching product data:', error);
		}
	}

	extractMetafieldValue(product) {
		if (!product.metafields) return null;

		// Try different possible structures
		if (product.metafields.custom?.solo_restricted !== undefined) {
			return product.metafields.custom.solo_restricted;
		}

		if (product.metafields.solo_restricted !== undefined) {
			return product.metafields.solo_restricted;
		}

		// Try to find in array format
		const soloMetafield = product.metafields.find?.((m) => m.namespace === 'custom' && m.key === 'solo_restricted');

		if (soloMetafield) {
			return soloMetafield.value === 'true' || soloMetafield.value === true;
		}

		return null;
	}

	getProductData(productId) {
		const productIdStr = String(productId);

		let data = window['soloProductData']?.cartItems?.[productIdStr] || window['soloProductData']?.cartItems?.[productId] || {};

		if (data.solo_restricted === undefined && window['currentProductData']) {
			if (String(window['currentProductData'].id) === productIdStr) {
				data = {
					id: window['currentProductData'].id,
					solo_restricted: window['currentProductData'].solo_restricted,
					metafield_value: window['currentProductData'].metafield_value,
					has_tag: window['currentProductData'].has_tag,
					tags: window['currentProductData'].tags,
				};
			}
		}

		return data;
	}

	isSoloRestricted(productData) {
		return productData.solo_restricted === true;
	}

	async removeRestrictedItems(items) {
		for (const item of items) {
			try {
				await this.updateCartItem(item.key, 0);
			} catch (error) {
				console.error('Error removing restricted item:', error);
			}
		}
	}

	showNotification() {
		if (window['theme']?.flash) {
			window['theme'].flash.show(this.notificationMessage, 'error');
			return;
		}

		this.createCustomNotification();
	}

	createCustomNotification() {
		const notification = document.createElement('div');
		notification.className = 'solo-restriction-notification';
		notification.textContent = this.notificationMessage;

		document.body.appendChild(notification);

		setTimeout(() => notification.classList.add('show'), 100);

		setTimeout(() => {
			notification.classList.add('hide');
			setTimeout(() => {
				if (notification.parentNode) {
					notification.parentNode.removeChild(notification);
				}
			}, 300);
		}, this.notificationDuration);
	}

	validate() {
		this.validateCart();
	}

	/**
	 * Updates Add to Cart button state based on cart contents
	 * Only updates if we're on a product page with a restricted product
	 */
	async updateAddToCartButtons() {
		// Only run on product pages
		if (!window['currentProductData']) {
			return;
		}

		const currentProduct = window['currentProductData'];

		// Only update if current product is restricted
		if (!currentProduct.solo_restricted) {
			return;
		}

		try {
			const cartData = await this.fetchCart();

			// Check if cart has non-restricted items
			let hasNonRestrictedItems = false;

			if (cartData.items && cartData.items.length > 0) {
				await this.ensureProductData(cartData.items);

				for (const item of cartData.items) {
					const productData = this.getProductData(item.product_id);

					// If this item is not the current product page product and is not restricted
					if (String(item.product_id) !== String(currentProduct.id) && !this.isSoloRestricted(productData)) {
						hasNonRestrictedItems = true;
						break;
					}
				}
			}

			// Update all Add to Cart buttons on the page
			const buttons = document.querySelectorAll('[data-add-to-cart], .product-form__cart-submit, button[name="add"]');

			buttons.forEach((button) => {
				if (!(button instanceof HTMLButtonElement)) {
					return;
				}

				const buttonText = button.querySelector('[data-add-to-cart-text]');
				const langAvailable = button.dataset.langAvailable || 'Add to cart';
				const langAddOtherItemsFirst = button.dataset.langAddOtherItemsFirst || 'Add other items first';

				if (hasNonRestrictedItems) {
					// Enable button - cart has non-restricted items
					button.removeAttribute('disabled');
					button.removeAttribute('aria-disabled');
					button.type = 'submit';
					button.classList.remove('btn--disabled');

					if (buttonText) {
						buttonText.textContent = langAvailable;
					}

					// Hide restriction warning if it exists
					const warning = button.closest('.product-form__item--submit')?.querySelector('.product-form__solo-restriction-warning');
					if (warning && warning instanceof HTMLElement) {
						warning.style.display = 'none';
					}
				} else {
					// Disable button - cart doesn't have non-restricted items
					button.setAttribute('disabled', 'disabled');
					button.setAttribute('aria-disabled', 'true');
					button.type = 'button';
					button.classList.add('btn--disabled');

					if (buttonText) {
						buttonText.textContent = langAddOtherItemsFirst;
					}

					// Show restriction warning if it exists
					const warning = button.closest('.product-form__item--submit')?.querySelector('.product-form__solo-restriction-warning');
					if (warning && warning instanceof HTMLElement) {
						warning.style.display = 'block';
					}
				}
			});
		} catch (error) {
			console.error('Error updating Add to Cart buttons:', error);
		}
	}

	setEnabled(enabled) {
		this.isEnabled = enabled;
		if (enabled) {
			this.validateCartWithDebounce();
		}
	}
};

let soloRestrictions = null;

function initializeSoloRestrictions() {
	if (soloRestrictions || window['soloRestrictions']) {
		return;
	}

	if (!window['SoloProductRestrictionsClass']) {
		return;
	}

	soloRestrictions = new window['SoloProductRestrictionsClass']();
	window['soloRestrictions'] = soloRestrictions;
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initializeSoloRestrictions);
} else {
	initializeSoloRestrictions();
}
